import { RelativePosition } from './RelativePosition';

/**
 * Represents a parent node that manages child nodes in relative positions
 * Handles spatial relationships and collision detection for child placement
 */
export class Parent {
  /**
   * Create a parent node
   * @param {{x: number, y: number}} position - Absolute position of parent
   * @param {string} id - Unique identifier for the parent
   */
  constructor(position, id) {
    this.position = position;
    this.id = id;
    this.children = []; // Array of {id: string, relativePosition: RelativePosition}
  }

  /**
   * Add a child at a relative position
   * @param {string} childId - Child node ID
   * @param {RelativePosition} relativePosition - Child's relative position
   */
  addChild(childId, relativePosition) {
    // Remove existing child if already present
    this.removeChild(childId);

    this.children.push({
      id: childId,
      relativePosition: relativePosition instanceof RelativePosition
        ? relativePosition
        : RelativePosition.fromJSON(relativePosition)
    });
  }

  /**
   * Remove a child by ID
   * @param {string} childId - Child node ID to remove
   * @returns {boolean} True if child was found and removed
   */
  removeChild(childId) {
    const index = this.children.findIndex(c => c.id === childId);
    if (index !== -1) {
      this.children.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Get child by ID
   * @param {string} childId - Child node ID
   * @returns {{id: string, relativePosition: RelativePosition}|null} Child object or null
   */
  getChildById(childId) {
    return this.children.find(c => c.id === childId) || null;
  }

  /**
   * Get child at exact angle
   * @param {number} angle - Angle in degrees
   * @returns {{id: string, relativePosition: RelativePosition}|null} Child object or null
   */
  getChildAtAngle(angle) {
    return this.children.find(c => c.relativePosition.angle === angle) || null;
  }

  /**
   * Get child within angle range and distance tolerance
   * @param {number} angle - Target angle in degrees
   * @param {number} angleBuffer - Angle tolerance in degrees
   * @param {number} targetDistance - Target distance
   * @param {number} distanceTolerance - Distance tolerance (default: 20)
   * @returns {{id: string, relativePosition: RelativePosition}|null} Child object or null
   */
  getChildAtAngleRange(angle, angleBuffer, targetDistance, distanceTolerance = 20) {
    const normalize = a => ((a % 360) + 360) % 360;
    const angleDiff = (a1, a2) => Math.min(
      Math.abs(normalize(a1) - normalize(a2)),
      360 - Math.abs(normalize(a1) - normalize(a2))
    );

    return this.children.find(child => {
      const { angle: childAngle, distance: childDistance } = child.relativePosition;
      return angleDiff(childAngle, angle) <= angleBuffer &&
             Math.abs(childDistance - targetDistance) <= distanceTolerance;
    }) || null;
  }

  /**
   * Update child position from absolute coordinates
   * @param {string} childId - Child node ID
   * @param {{x: number, y: number}} newAbsolutePosition - New absolute position
   * @returns {boolean} True if child was found and updated
   */
  updateChildPosition(childId, newAbsolutePosition) {
    const child = this.getChildById(childId);
    if (!child) return false;

    child.relativePosition = RelativePosition.fromPoint(
      this.position.x,
      this.position.y,
      newAbsolutePosition.x,
      newAbsolutePosition.y
    );
    return true;
  }

  /**
   * Get all children sorted by angle
   * @returns {Array<{id: string, relativePosition: RelativePosition}>} Sorted children
   */
  getChildrenByAngle() {
    return [...this.children].sort((a, b) => a.relativePosition.angle - b.relativePosition.angle);
  }

  /**
   * Get children within distance range
   * @param {number} minDistance - Minimum distance
   * @param {number} maxDistance - Maximum distance
   * @returns {Array<{id: string, relativePosition: RelativePosition}>} Filtered children
   */
  getChildrenInDistanceRange(minDistance, maxDistance) {
    return this.children.filter(child => {
      const distance = child.relativePosition.distance;
      return distance >= minDistance && distance <= maxDistance;
    });
  }

  /**
   * Get absolute positions of all children
   * @returns {Array<{id: string, position: {x: number, y: number}}>} Children with absolute positions
   */
  getChildrenAbsolutePositions() {
    return this.children.map(child => ({
      id: child.id,
      position: child.relativePosition.getPointFrom(this.position.x, this.position.y)
    }));
  }

  /**
   * Update parent position and recalculate all child relative positions
   * @param {{x: number, y: number}} newPosition - New parent position
   * @param {Array<{id: string, position: {x: number, y: number}}>} childAbsolutePositions - Current child absolute positions
   */
  updatePosition(newPosition, childAbsolutePositions = []) {
    this.position = newPosition;

    // Update relative positions based on new parent position
    childAbsolutePositions.forEach(({ id, position }) => {
      this.updateChildPosition(id, position);
    });
  }

  /**
   * Find best angle for new child to avoid collisions
   * @param {number} preferredAngle - Preferred angle in degrees
   * @param {number} distance - Distance from parent
   * @param {number} angleBuffer - Minimum angle between children (default: 30)
   * @returns {number} Best available angle
   */
  findBestAngleForChild(preferredAngle, distance, angleBuffer = 30) {
    let angle = preferredAngle;
    const maxIterations = Math.ceil(360 / 15); // Check every 15 degrees

    for (let i = 0; i < maxIterations; i++) {
      if (!this.getChildAtAngleRange(angle, angleBuffer / 2, distance)) {
        return angle;
      }
      angle = (angle + 15) % 360;
    }

    return preferredAngle; // Return original if no better angle found
  }

  /**
   * Serialize to plain object
   * @returns {Object} Serialized parent data
   */
  toJSON() {
    return {
      position: this.position,
      id: this.id,
      children: this.children.map(child => ({
        id: child.id,
        relativePosition: child.relativePosition.toJSON()
      }))
    };
  }

  /**
   * Create Parent from serialized data
   * @param {Object} data - Serialized parent data
   * @returns {Parent} New Parent instance
   */
  static fromJSON(data) {
    const parent = new Parent(data.position, data.id);
    parent.children = data.children.map(child => ({
      id: child.id,
      relativePosition: RelativePosition.fromJSON(child.relativePosition)
    }));
    return parent;
  }

  /**
   * Get the number of children
   * @returns {number} Number of children
   */
  get childCount() {
    return this.children.length;
  }

  /**
   * Check if parent has any children
   * @returns {boolean} True if parent has children
   */
  get hasChildren() {
    return this.children.length > 0;
  }
}